import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AVAILABILITY_OPTIONS, MOBILITY_OPTIONS } from "@/lib/constants";
import { Logo } from "@/components/Logo";
import { MapPin, Search, Users, Eye, EyeOff, Video, FolderOpen } from "lucide-react";

const EMPTY_FILTERS = {
  q: "", profession: "", skill: "", country: "", language: "",
  min_experience: "", availability: "", mobility: "", desired_country: "", education: "",
  has_video: "", has_portfolio: "", demo_skill: "",
};

export default function TalentPool() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  const allowed = user && ["super_admin", "admin_lcs", "recruteur_lcs", "assistant_recruteur", "employeur", "manager_employeur"].includes(user.role);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    if (!loading && user && !allowed) navigate("/dashboard", { replace: true });
  }, [user, loading, allowed, navigate]);

  const search = useCallback(async () => {
    if (!allowed) return;
    setSearching(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v != null) params[k] = v;
      });
      const res = await api.get("/talent/search", { params });
      setResults(res.data);
      setError("");
    } catch {
      setError("Erreur lors de la recherche.");
    } finally {
      setSearching(false);
    }
  }, [filters, allowed]);

  useEffect(() => {
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [search]);

  if (!user || !allowed) return null;

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const inputCls =
    "border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent";

  return (
    <div data-testid="talent-pool-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} data-testid="talent-logo-link" aria-label="Retour au tableau de bord">
            <Logo />
          </button>
          <button
            data-testid="talent-back-button"
            onClick={() => navigate("/dashboard")}
            className="text-sm text-slate-600 hover:text-brand-navy border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-100 transition-colors"
          >
            Tableau de bord
          </button>
        </div>
      </header>

      <div className="bg-brand-navy py-10">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Users className="w-7 h-7 text-brand-turquoise" />
            Talent Pool international
          </h1>
          <p className="text-white/60">Recherchez des candidats par métier, compétences, pays, langues et disponibilité.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <input data-testid="talent-q-input" value={filters.q} onChange={set("q")} placeholder="Mot-clé…" className={inputCls} />
          <input data-testid="talent-profession-input" value={filters.profession} onChange={set("profession")} placeholder="Métier" className={inputCls} />
          <input data-testid="talent-skill-input" value={filters.skill} onChange={set("skill")} placeholder="Compétence" className={inputCls} />
          <input data-testid="talent-country-input" value={filters.country} onChange={set("country")} placeholder="Pays de résidence" className={inputCls} />
          <input data-testid="talent-language-input" value={filters.language} onChange={set("language")} placeholder="Langue" className={inputCls} />
          <input data-testid="talent-experience-input" type="number" min={0} value={filters.min_experience} onChange={set("min_experience")} placeholder="Exp. min (ans)" className={inputCls} />
          <select data-testid="talent-availability-select" value={filters.availability} onChange={set("availability")} className={inputCls}>
            <option value="">Disponibilité</option>
            {AVAILABILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select data-testid="talent-mobility-select" value={filters.mobility} onChange={set("mobility")} className={inputCls}>
            <option value="">Mobilité</option>
            {MOBILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input data-testid="talent-desired-country-input" value={filters.desired_country} onChange={set("desired_country")} placeholder="Pays souhaité" className={inputCls} />
          <input data-testid="talent-education-input" value={filters.education} onChange={set("education")} placeholder="Formation" className={inputCls} />
          <select data-testid="talent-has-video-select" value={filters.has_video} onChange={set("has_video")} className={inputCls}>
            <option value="">Vidéo : tous</option>
            <option value="oui">Avec vidéo de démonstration</option>
          </select>
          <select data-testid="talent-has-portfolio-select" value={filters.has_portfolio} onChange={set("has_portfolio")} className={inputCls}>
            <option value="">Portfolio : tous</option>
            <option value="oui">Avec portfolio partagé</option>
          </select>
          <input data-testid="talent-demo-skill-input" value={filters.demo_skill} onChange={set("demo_skill")} placeholder="Compétence démontrée (portfolio)" className={inputCls} />
        </div>

        {error && <p data-testid="talent-error" className="text-sm text-red-600 mb-4">{error}</p>}
        <p data-testid="talent-count" className="text-sm text-slate-500 mb-4">
          {searching ? "Recherche…" : `${results.length} profil(s) trouvé(s)`}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((cv) => (
            <div key={cv.cv_id} data-testid={`talent-card-${cv.cv_id}`} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-turquoise transition-colors">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="font-semibold text-brand-navy">{cv.full_name}</p>
                {cv.visibility === "private" ? (
                  <EyeOff className="w-4 h-4 text-slate-300" aria-label="Profil privé" />
                ) : (
                  <Eye className="w-4 h-4 text-brand-turquoise" aria-label="Profil public" />
                )}
              </div>
              <p className="text-sm text-slate-600 mb-1">{cv.profession}</p>
              <p className="text-xs text-slate-400 flex items-center gap-1 mb-3">
                <MapPin className="w-3 h-3" />
                {[cv.city, cv.country].filter(Boolean).join(", ") || "—"} · {cv.experience_years} an(s)
              </p>
              {cv.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {cv.skills.slice(0, 4).map((s) => (
                    <span key={s} className="text-xs bg-brand-sky text-brand-navy rounded-full px-2.5 py-0.5">{s}</span>
                  ))}
                  {cv.skills.length > 4 && (
                    <span className="text-xs text-slate-400">+{cv.skills.length - 4}</span>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {cv.languages?.map((l) => (
                  <span key={l} className="text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">{l}</span>
                ))}
              </div>
              {cv.desired_countries?.length > 0 && (
                <p className="text-xs text-slate-400 mt-2">Recherche : {cv.desired_countries.join(", ")}</p>
              )}
              {cv.portfolio && cv.portfolio.shared > 0 && (
                <div data-testid={`talent-portfolio-badges-${cv.cv_id}`} className="flex flex-wrap items-center gap-1.5 mt-2">
                  {cv.portfolio.has_video && (
                    <span className="inline-flex items-center gap-1 text-xs bg-brand-navy text-white rounded-full px-2.5 py-0.5">
                      <Video className="w-3 h-3" /> Vidéo
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs bg-brand-turquoise/10 text-brand-turquoise rounded-full px-2.5 py-0.5">
                    <FolderOpen className="w-3 h-3" /> {cv.portfolio.shared} réalisation(s)
                  </span>
                </div>
              )}
              {cv.owner_id && (
                <button data-testid={`talent-view-portfolio-${cv.cv_id}`}
                  onClick={() => navigate(`/talent/candidat/${cv.owner_id}`)}
                  className="mt-3 text-xs font-semibold text-brand-blue hover:text-brand-turquoise transition-colors">
                  Voir le profil →
                </button>
              )}
            </div>
          ))}
        </div>
        {!searching && results.length === 0 && (
          <p data-testid="talent-empty" className="text-center text-slate-400 bg-white rounded-xl border border-slate-200 p-12">
            Aucun profil ne correspond à votre recherche.
          </p>
        )}
      </div>
    </div>
  );
}
